/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateCropAttributeMutation,
  useDeleteCropAttributeMutation,
  useGetCropAttributesQuery,
} from '../../api/cropsApi';
import type {
  CropAttributeCreatePayload,
  CropAttributeOptionInput,
  CropAttributeSummary,
  CropAttributeScopeType,
} from '../../models/Crop';
import { toastError, toastSuccess } from '../../utils/toast';
import { useAttributeEditor } from '../../components/attributes/useAttributeEditor';
import { EditAttributeModal } from '../../components/attributes/EditAttributeModal';
import { AttributeOptionsEditor } from '../../components/attributes/AttributeOptionsEditor';

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css`
    margin: 0;
  `,
  paragraphNoMargin: css`
    margin: 0;
  `,
  selectWidth220: css`
    width: 220px;
  `,
  searchMaxWidth: css`
    max-width: 320px;
  `,
};

const AdminAttributesCatalogPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [scopeFilter, setScopeFilter] = useState<CropAttributeScopeType | 'all'>('system');
  const [search, setSearch] = useState('');

  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);
  const adminScopeId = useSelector(
    (state: any) => state.saves?.activeSaveId || ''
  ) as string;

  const canManageCropAttributes = useMemo(() => {
    if (!user) return false;
    if (user.role && ['superadmin', 'superuser'].includes(String(user.role).toLowerCase())) {
      return true;
    }
    if (Array.isArray(userActions)) {
      return (
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update')
      );
    }
    return false;
  }, [user, userActions]);

  const attributeQueryParams = useMemo(() => {
    const params: any = { saveId: adminScopeId };
    if (scopeFilter !== 'all') {
      params.scopeType = scopeFilter;
    }
    return params;
  }, [scopeFilter, adminScopeId]);

  const {
    data: attributes = [],
    isFetching: attributesLoading,
    isError: attributesError,
    refetch: refetchAttributes,
  } = useGetCropAttributesQuery(attributeQueryParams, { skip: !adminScopeId });

  const {
    editForm,
    editModalOpen,
    isLoadingAttribute,
    handleCloseEditModal,
    handleEditFormValuesChange,
    handleOpenEditAttribute,
    handleUpdateFarmAttribute,
  } = useAttributeEditor(refetchAttributes);

  const filteredAttributes = useMemo(() => {
    const list = attributes as CropAttributeSummary[];
    if (!search.trim()) return list;
    const normalized = search.trim().toLowerCase();
    return list.filter((attribute) => {
      const nameMatch = attribute.name.toLowerCase().includes(normalized);
      const descriptionMatch = (attribute.description ?? '').toLowerCase().includes(normalized);
      return nameMatch || descriptionMatch;
    });
  }, [attributes, search]);

  const [createAttribute, { isLoading: creatingAttribute }] = useCreateCropAttributeMutation();
  const [deleteAttribute, { isLoading: deletingAttribute }] = useDeleteCropAttributeMutation();

  const handleOpenCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ scopeType: 'system' });
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      const options: CropAttributeOptionInput[] = (values.options || [])
        .map((item: any, index: number) => {
          const title = item?.title?.trim();
          if (!title) return null;
          const code = (item?.code ?? '').trim() || title;
          return { title, code, sortOrder: index };
        })
        .filter((option): option is CropAttributeOptionInput => option !== null);

      if (!adminScopeId && values.scopeType !== 'system') {
        toastError(t('attributesCatalog.errors.noActiveSave'));
        return;
      }
      const payload: CropAttributeCreatePayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        scopeType: values.scopeType ?? 'system',
        options: options.length ? options : null,
        binding: null,
      };

      await createAttribute({ ...payload, saveId: adminScopeId }).unwrap();
      toastSuccess(t('attributesCatalog.messages.created'));
      setCreateModalOpen(false);
      createForm.resetFields();
      refetchAttributes();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleDelete = useCallback((attribute: CropAttributeSummary) => {
    Modal.confirm({
      title: t('attributesCatalog.delete.title', { name: attribute.name }),
      icon: <ExclamationCircleOutlined />,
      content: t('attributesCatalog.delete.content'),
      okText: t('common:actions.delete'),
      cancelText: t('common:actions.cancel'),
      okButtonProps: { danger: true, loading: deletingAttribute },
      onOk: async () => {
        if (!adminScopeId) {
          toastError(t('attributesCatalog.errors.noActiveSave'));
          return;
        }
        try {
          await deleteAttribute({ id: attribute.id, saveId: adminScopeId }).unwrap();
          toastSuccess(t('attributesCatalog.messages.deleted'));
          refetchAttributes();
        } catch (error) {
          toastError(error);
        }
      },
    });
  }, [deleteAttribute, deletingAttribute, refetchAttributes, adminScopeId, t]);

  const columns: ColumnsType<CropAttributeSummary> = useMemo(
    () => [
      {
        title: t('attributesCatalog.columns.name'),
        dataIndex: 'name',
        render: (value: string, record) => (
          <Space direction="vertical" size={2}>
            <Text strong>{value}</Text>
            {record.description && <Text type="secondary">{record.description}</Text>}
          </Space>
        ),
      },
      {
        title: t('common:labels.type'),
        dataIndex: 'scopeType',
        width: 120,
        render: (value: CropAttributeScopeType | string | undefined, record) => {
          const scopeType = value ?? 'system';
          const color = scopeType === 'save' ? 'green' : 'purple';
          const label = scopeType === 'save'
            ? t('attributesCatalog.scope.save')
            : t('attributesCatalog.scope.system');
          return (
            <Space direction="vertical" size={2}>
              <Tag color={color}>{label}</Tag>
              {scopeType === 'save' && record.saveId != null && (
                <Text type="secondary">{t('attributesCatalog.saveId', { id: record.saveId })}</Text>
              )}
            </Space>
          );
        },
      },
      {
        title: t('attributesCatalog.columns.options'),
        render: (_, record) => {
          if (!record.options || record.options.length === 0) {
            return <Text type="secondary">{t('attributesCatalog.optionsNotSet')}</Text>;
          }
          return (
            <Space size={[4, 4]} wrap>
              {record.options.map((option) => (
                <Tag key={`${record.id}-${option.id}`}>
                  {option.code && option.code !== option.title
                    ? `${option.title} (${option.code})`
                    : option.title}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: t('common:labels.actions'),
        width: 160,
        render: (_, record) => (
          <Space>
            <Button type="link" onClick={() => handleOpenEditAttribute(record)}>
              {t('common:actions.edit')}
            </Button>
            <Button type="link" danger onClick={() => handleDelete(record)}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        ),
      },
    ],
    [handleDelete, handleOpenEditAttribute, t],
  );

  if (!canManageCropAttributes) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: t('attributesCatalog.breadcrumb.admin') },
              { title: t('attributesCatalog.title') },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta
            title={t('attributesCatalog.noAccess.title')}
            description={t('attributesCatalog.noAccess.description')}
          />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: t('attributesCatalog.breadcrumb.admin') },
            { title: t('attributesCatalog.title') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
            <Title level={4} css={styles.titleNoMargin}>
              {t('attributesCatalog.title')}
            </Title>
            <Paragraph type="secondary" css={styles.paragraphNoMargin}>
              {t('attributesCatalog.intro')}
            </Paragraph>
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <Space css={commonStyles.fullWidth} align="center" wrap>
              <Select
                value={scopeFilter}
                onChange={(value) => setScopeFilter(value)}
                css={styles.selectWidth220}
                options={[
                  { value: 'system', label: t('attributesCatalog.filter.system') },
                  { value: 'farm', label: t('attributesCatalog.filter.farm') },
                  { value: 'all', label: t('attributesCatalog.filter.all') },
                ]}
              />
              <Input.Search
                placeholder={t('attributesCatalog.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
                css={styles.searchMaxWidth}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
                {t('attributesCatalog.add')}
              </Button>
            </Space>

            {attributesLoading ? (
              <Spin />
            ) : attributesError ? (
              <Card type="inner" title={t('attributesCatalog.loadError.title')}>
                {t('attributesCatalog.loadError.description')}
              </Card>
            ) : filteredAttributes.length === 0 ? (
              <Empty description={t('attributesCatalog.empty')} />
            ) : (
              <Table<CropAttributeSummary>
                rowKey={(record) => String(record.id)}
                dataSource={filteredAttributes}
                columns={columns}
                pagination={false}
              />
            )}
          </Space>
        </Card>
      </Space>

      <Modal
        title={t('attributesCatalog.add')}
        open={createModalOpen}
        onOk={handleCreateSubmit}
        onCancel={() => setCreateModalOpen(false)}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={creatingAttribute}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t('attributesCatalog.columns.name')}
            name="name"
            rules={[{ required: true, message: t('attributesCatalog.form.nameRequired') }]}
          >
            <Input placeholder={t('attributesCatalog.form.namePlaceholder')} />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} placeholder={t('attributesCatalog.form.descriptionPlaceholder')} allowClear />
          </Form.Item>
          <Form.Item label={t('common:labels.type')} name="scopeType" initialValue="system" rules={[{ required: true, message: t('attributesCatalog.form.scopeRequired') }]}
            tooltip={t('attributesCatalog.form.scopeTooltip')}>
            <Select
              options={[
                { value: 'system', label: t('attributesCatalog.scope.system') },
                { value: 'save', label: t('attributesCatalog.scope.save') },
              ]}
            />
          </Form.Item>
          <Form.List name="options">
            {(fields, operations) => (
              <AttributeOptionsEditor
                fields={fields}
                operations={operations}
                helperText={t('attributesCatalog.form.optionsHelper')}
                emptyText={t('attributesCatalog.form.optionsEmpty')}
                listName={['options']}
              />
            )}
          </Form.List>
        </Form>
      </Modal>

      <EditAttributeModal
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        onOk={handleUpdateFarmAttribute}
        form={editForm}
        onValuesChange={handleEditFormValuesChange}
        loading={isLoadingAttribute}
      />
    </MainLayout>
  );
};

export default AdminAttributesCatalogPage;
